
































































import { Component, Inject, Prop, Vue, Watch } from 'vue-property-decorator';

import {
	Product,
	ProductAccessory,
	ProductSimProJobTypeEnum,
	Sensor,
	SimproJobDetails,
	SimproJobEntityGroupEntityTypeEnum,
	Transceiver,
	Vat,
} from '@/api';
import EntityHeader from '@/components/entity-header/EntityHeader.vue';
import MilkVatChartsTab from './MilkVatChartsTab.vue';
import ProvisionVatModal from './ProvisionVatModal.vue';
import InstallVatModal from './InstallVatModal.vue';
import MilkVatChangeLog from './MilkVatChangeLog.vue';
import MilkVatInfoLine from './MilkVatInfoLine.vue';
import MilkVatOffsetChangeLog from './MilkVatOffsetChangeLog.vue';
import EditEmptyDistanceModal from './EditEmptyDistanceModal.vue';
import EditTempOffsetModal from './EditTempOffsetModal.vue';
import MilkVatGeneralInfo from './MilkVatGeneralInfo.vue';
import EditVatStateModal from './EditVatStateModal.vue';

import { EntityStatusService } from '@/services/entity-status-service';
import { MilkVatStatusService } from '@/services/milk-vat-status-service';
import Toast from '@/components/display/toast.vue';

import { VatService } from './service/vat-service';
import { VatModals } from './models/vat-modals';
import { VatDashboard } from './models/vat-dashboard';
import EntityAccessories from '@/components/entityAccessories.vue';
import SaveCancelButtons from '@/components/elements/buttons/save-cancel-buttons.vue';
import {
	StatusChangeReasonsCancel,
	StatusChangeReasonsLastEntity,
	StatusChangeReasonsRemoval,
} from '@/common/Constants';
import { isLastEntityOnProduct } from '@/common/EntityUtils';
import MilkVatCorrections from './corrections/MilkVatCorrections.vue';

@Component({
	computed: {
		SimproJobEntityGroupEntityTypeEnum() {
			return SimproJobEntityGroupEntityTypeEnum;
		},
	},
	components: {
		MilkVatCorrections,
		SaveCancelButtons,
		EntityAccessories,
		EntityHeader,
		MilkVatChartsTab,
		ProvisionVatModal,
		InstallVatModal,
		MilkVatChangeLog,
		MilkVatInfoLine,
		MilkVatOffsetChangeLog,
		EditEmptyDistanceModal,
		EditTempOffsetModal,
		MilkVatGeneralInfo,
		EditVatStateModal,
	},
})
export default class MilkVatDetail extends Vue {
	@Prop({ required: true, type: Object })
	public product!: Product;

	@Prop({ required: true, type: Object })
	public vat!: Vat & { transceiver: Transceiver; sensors: Sensor[] };

	@Prop({ required: true, type: Object })
	public readonly dashboardData!: VatDashboard;

	@Prop({ required: false, type: Array })
	public readonly accessories: ProductAccessory[] | undefined;

	@Prop({ required: false, type: Array })
	public readonly accessoriesForInstall: ProductAccessory[] | undefined;

	@Prop({ required: false, type: Array })
	public readonly simproJobs?: SimproJobDetails[];

	@Inject('refreshMilk')
	private refresh!: () => Promise<void>;

	public milkVatStatusService: EntityStatusService<Vat> = new MilkVatStatusService(this.vat);
	public emptyDistanceModalId: string = `${VatModals.EMPTY_VOLUME}-${this.vat.id}`;
	public tempOffsetModalId: string = `${VatModals.TEMP_OFFSET}-${this.vat.id}`;
	public editVatStateModalId: string = VatModals.STATE;

	@Watch('vat')
	public onVatChanged(newVat: typeof this.vat): void {
		this.milkVatStatusService = new MilkVatStatusService(newVat);
	}

	public get title(): string {
		return this.vat.label ?? 'No label';
	}

	public get subTitle(): string {
		const formula: string = Vue.filter('titleCase')(this.vat.formula ?? 'No formula');
		const size: string = this.vat.sizeString ?? 'No size';
		const state: string = Vue.filter('titleCase')(this.vat.state ?? 'No state');
		return `${formula.replace('Ii', 'II')} - ${size} | ${state}`;
	}

	private hasSimproRemovalJobReason() {
		return this.simproJobs?.find((simproJob: SimproJobDetails) => {
			return simproJob.jobType === ProductSimProJobTypeEnum.Remove && simproJob.reason != null;
		});
	}

	public statusChangeReasonOptions(removal: boolean) {
		return this.hasSimproRemovalJobReason()
			? []
			: (isLastEntityOnProduct(this.product)
				? StatusChangeReasonsLastEntity
				: removal
					? StatusChangeReasonsRemoval
					: StatusChangeReasonsCancel
			).map(val => ({ value: val, text: val }));
	}

	private vatService: VatService = new VatService();
	private toast: Toast = new Toast();

	public async cancelVat(params: {
		modalId?: string;
		actionReason?: string | null;
	}): Promise<void> {
		const cancelResult = await this.vatService.cancelVat(this.vat, params.actionReason);
		if (cancelResult?.errorMessage) {
			this.toast.makeToast(cancelResult.errorMessage, 'Error', 'danger');
		} else {
			this.toast.makeToast('Vat cancelled', 'Success', 'success');
			if (params?.modalId) this.$bvModal.hide(params.modalId);
			await this.refresh();
		}
	}

	public async removeVat(params: {
		modalId?: string;
		actionReason?: string | null;
	}): Promise<void> {
		const removalResult = await this.vatService.removeVat(this.vat, params.actionReason);
		if (removalResult?.errorMessage) {
			this.toast.makeToast(removalResult.errorMessage, 'Error', 'danger');
		} else {
			this.toast.makeToast('Vat removed', 'Success', 'success');
			if (params?.modalId) this.$bvModal.hide(params.modalId);
			await this.refresh();
		}
	}

	public openProvisioningModal(): void {
		this.$bvModal.show(VatModals.PROVISION);
	}

	public openInstallModal(): void {
		this.$bvModal.show(VatModals.INSTALL);
	}

	public openEmptyDistanceModal(): void {
		this.$bvModal.show(this.emptyDistanceModalId);
	}

	public async handleEmptyDistanceChange() {
		await this.refresh();
	}

	public async provision(vat: Vat): Promise<void> {
		const provisionResult = await this.vatService.provision(vat);
		if (provisionResult?.errorMessage) {
			this.toast.makeToast(provisionResult.errorMessage, 'Error provisioning vat', 'danger');
		} else {
			this.toast.makeToast('Vat provisioned', 'Success', 'success');
			this.$bvModal.hide(VatModals.PROVISION);
			await this.refresh();
		}
	}

	public async install(vat: Vat & { transceiver: Transceiver; sensors: Sensor[] }): Promise<void> {
		const installResult = await this.vatService.install(vat);
		if (installResult?.errorMessage) {
			this.toast.makeToast(installResult.errorMessage, 'Error installing vat', 'danger');
		} else {
			this.toast.makeToast('Vat installed', 'Success', 'success');
			this.$bvModal.hide(VatModals.INSTALL);
			await this.refresh();
		}
	}

	public async signOff(vat: Vat): Promise<void> {
		const signOffResult = await this.vatService.signOffVat(vat);
		if (signOffResult?.errorMessage) {
			this.toast.makeToast(signOffResult.errorMessage, 'Error signing off vat', 'danger');
		} else {
			this.toast.makeToast('Vat signed off', 'Success', 'success');
			await this.refresh();
		}
	}

	public async moveToSignOff(vat: Vat): Promise<void> {
		const moveToSignOffResult = await this.vatService.moveVatToSignOff(vat);
		if (moveToSignOffResult?.errorMessage) {
			this.toast.makeToast(
				moveToSignOffResult.errorMessage,
				'Error moving vat to sign off',
				'danger',
			);
		} else {
			this.toast.makeToast('Vat moved to sign off', 'Success', 'success');
			await this.refresh();
		}
	}

	public async closeMilkScore() {
		const response = await this.vatService.closeMilkScore(this.vat.id);
		if (response.errorMessage) {
			this.toast.makeToast(response.errorMessage, 'Close Milk Score Error', 'danger');
		} else {
			this.toast.makeToast('Milk Score Closed', 'Success', 'success');
		}
	}

	public async onVatStateChanged(): Promise<void> {
		try {
			await this.vatService.updateVatState(this.vat);
			this.toast.makeToast('Vat state updated', 'Success', 'success');
			await this.refresh();
		} catch (e) {
			this.toast.makeToast('Error', 'There was a problem updating the vat state', 'danger', false);
		}
	}

	private dismiss(modal: string) {
		setTimeout(() => this.$bvModal.hide(modal), 1);
	}
}
